// The sidebar's Fit and Engagement line graph (GRO-381): the geometry as plain data, so the
// component is only markup and the maths is testable without a DOM.

import type { ScorePoint } from '../api/types.ts';

export interface GraphLine {
  key: 'fit' | 'engagement';
  // An SVG path, split where a day has no score, so a gap is a gap rather than a drop to 0.
  path: string;
  // The last plotted point, for the end dot.
  lastX: number | null;
  lastY: number | null;
}

export interface GraphGeometry {
  lines: GraphLine[];
  firstDay: string;
  lastDay: string;
  // True when there are fewer than two days of history: nothing to draw as a line yet.
  tooFew: boolean;
}

export const GRAPH_WIDTH = 280;
export const GRAPH_HEIGHT = 84;
export const GRAPH_PAD_X = 4;
export const GRAPH_PAD_Y = 6;
const SCORE_MAX = 100;

function dayNumber(day: string): number {
  return Date.parse(day + 'T00:00:00Z') / 86_400_000;
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

function lineFor(key: 'fit' | 'engagement', points: ScorePoint[], firstDay: number, spanDays: number): GraphLine {
  const usableWidth = GRAPH_WIDTH - 2 * GRAPH_PAD_X;
  const usableHeight = GRAPH_HEIGHT - 2 * GRAPH_PAD_Y;
  let path = '';
  let penDown = false;
  let lastX: number | null = null;
  let lastY: number | null = null;

  // Scores run 0 to 100 bottom to top; days are spaced by calendar distance, not by row.
  for (const point of points) {
    const value = key === 'fit' ? point.fit_score : point.engagement_score;
    if (value == null || !Number.isFinite(value)) {
      penDown = false;
      continue;
    }
    const x = round(GRAPH_PAD_X + ((dayNumber(point.scored_on) - firstDay) / spanDays) * usableWidth);
    const clamped = Math.min(SCORE_MAX, Math.max(0, value));
    const y = round(GRAPH_PAD_Y + (1 - clamped / SCORE_MAX) * usableHeight);
    path += `${penDown ? 'L' : 'M'}${x} ${y} `;
    penDown = true;
    lastX = x;
    lastY = y;
  }
  return { key, path: path.trim(), lastX, lastY };
}

export function graphGeometry(points: ScorePoint[]): GraphGeometry {
  const ordered = [...points].sort((first, second) => first.scored_on.localeCompare(second.scored_on));
  if (ordered.length < 2) {
    const onlyDay = ordered[0]?.scored_on ?? '';
    return { lines: [], firstDay: onlyDay, lastDay: onlyDay, tooFew: true };
  }
  const firstDay = dayNumber(ordered[0]!.scored_on);
  const spanDays = Math.max(1, dayNumber(ordered[ordered.length - 1]!.scored_on) - firstDay);
  return {
    lines: [lineFor('fit', ordered, firstDay, spanDays), lineFor('engagement', ordered, firstDay, spanDays)],
    firstDay: ordered[0]!.scored_on,
    lastDay: ordered[ordered.length - 1]!.scored_on,
    tooFew: false,
  };
}
